<template>
  <div class="pycad-hud">
    <div class="pycad-hud__row pycad-hud__row--top">
      <div class="pycad-hud__cell pycad-hud__cell--tl">
        <span class="pycad-hud__label" :style="{ color: labelColor }">{{
          label
        }}</span>
        <span v-if="patientName" class="pycad-hud__patient">{{
          patientName
        }}</span>
        <span v-if="patientMeta" class="pycad-hud__meta">{{
          patientMeta
        }}</span>
        <span v-if="studyDate" class="pycad-hud__meta">{{ studyDate }}</span>
      </div>
      <div class="pycad-hud__cell pycad-hud__cell--tc">
        <span v-if="topOrientation" class="pycad-hud__orient">{{
          topOrientation
        }}</span>
      </div>
      <div class="pycad-hud__cell pycad-hud__cell--tr">
        <slot name="top-right" />
      </div>
    </div>

    <div class="pycad-hud__row pycad-hud__row--middle">
      <div class="pycad-hud__cell pycad-hud__cell--ml">
        <span v-if="leftOrientation" class="pycad-hud__orient">{{
          leftOrientation
        }}</span>
      </div>
      <div class="pycad-hud__cell" />
      <div class="pycad-hud__cell" />
    </div>

    <div
      class="pycad-hud__row pycad-hud__row--bottom"
      :style="{ paddingBottom: bottomPad }"
    >
      <div class="pycad-hud__cell pycad-hud__cell--bl">
        <template v-if="showSlice && sliceInfo">
          <span class="pycad-hud__data"
            >Im: {{ sliceInfo.current }} / {{ sliceInfo.total }}</span
          >
        </template>
        <template v-if="showWindowLevel && windowLevel">
          <span class="pycad-hud__data"
            >W: {{ windowLevel.width }} L: {{ windowLevel.level }}</span
          >
        </template>
        <slot name="bottom-left" />
      </div>
      <div class="pycad-hud__cell" />
      <div class="pycad-hud__cell pycad-hud__cell--br">
        <slot name="bottom-right" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface SliceInfo {
  current: number;
  total: number;
}
interface WindowLevel {
  width: string;
  level: string;
}

withDefaults(
  defineProps<{
    label: string;
    labelColor?: string;
    topOrientation?: string;
    leftOrientation?: string;
    patientName?: string;
    patientMeta?: string;
    studyDate?: string;
    sliceInfo?: SliceInfo;
    windowLevel?: WindowLevel;
    showSlice?: boolean;
    showWindowLevel?: boolean;
    /** Extra bottom padding to clear the MPR slice strip if rendered. */
    bottomPad?: string;
  }>(),
  {
    labelColor: '#ff9900',
    showSlice: true,
    showWindowLevel: true,
    bottomPad: '10px',
    patientName: '',
    patientMeta: '',
    studyDate: '',
    topOrientation: '',
    leftOrientation: '',
  }
);
</script>

<style scoped>
.pycad-hud {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  padding: 8px 10px;
  z-index: 12;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  -webkit-font-smoothing: antialiased;
}

.pycad-hud__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: flex-start;
}

.pycad-hud__row--middle {
  flex: 1;
  align-items: center;
}

.pycad-hud__row--bottom {
  align-items: flex-end;
}

.pycad-hud__cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pycad-hud__cell--tl,
.pycad-hud__cell--bl,
.pycad-hud__cell--ml {
  align-items: flex-start;
}

.pycad-hud__cell--bl {
  pointer-events: auto;
}

.pycad-hud__cell--tc {
  align-items: center;
}

.pycad-hud__cell--tr,
.pycad-hud__cell--br {
  align-items: flex-end;
  pointer-events: auto;
}

.pycad-hud__label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
}

.pycad-hud__orient {
  font-size: 12px;
  font-weight: 700;
  color: #d9d9d9;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.85);
  letter-spacing: 0.05em;
}

.pycad-hud__patient {
  font-size: 11px;
  font-weight: 500;
  color: #d9d9d9;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pycad-hud__meta {
  font-size: 10px;
  color: #a0a0a0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.pycad-hud__data {
  font-size: 11px;
  color: #bdbdbd;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  font-variant-numeric: tabular-nums;
}
</style>
